/**
 * Electron secure-storage shim.
 *
 * When running inside the packaged desktop app, this module hijacks the
 * Supabase auth token key(s) in `window.localStorage` and routes them to the
 * OS keychain via Electron's safeStorage bridge (see electron/preload.cjs).
 *
 * MUST be imported BEFORE any module that imports `@supabase/supabase-js`,
 * so it runs before `createClient` reads the persisted session.
 *
 * In a normal browser this file is a no-op.
 */

type SecureStorage = {
  isAvailable: () => boolean;
  get: (key: string) => string | null;
  set: (key: string, value: string) => boolean;
  remove: (key: string) => boolean;
};

declare global {
  interface Window {
    electronSecureStorage?: SecureStorage;
    electronRuntime?: { isElectron: boolean; platform: string };
  }
}

// Route any Supabase auth key (`sb-<ref>-auth-token`) through safeStorage.
// Also cover the code-verifier used during OAuth PKCE flows.
const shouldIntercept = (key: string) =>
  /^sb-[a-z0-9-]+-auth-token(\.\d+)?$/i.test(key) ||
  /^sb-[a-z0-9-]+-auth-token-code-verifier$/i.test(key);

function install() {
  if (typeof window === "undefined") return;
  const secure = window.electronSecureStorage;
  if (!secure || !window.electronRuntime?.isElectron) return;
  if (!secure.isAvailable()) {
    // OS keychain unavailable (rare on Linux without libsecret/kwallet).
    // Leave localStorage as-is; Electron will still persist across restarts,
    // just without OS-level encryption.
    console.warn("[secure-storage] OS keychain unavailable — using plain localStorage");
    return;
  }

  const proto = Object.getPrototypeOf(window.localStorage) as Storage;
  const origGet = proto.getItem.bind(window.localStorage);
  const origSet = proto.setItem.bind(window.localStorage);
  const origRemove = proto.removeItem.bind(window.localStorage);

  // One-time migration: if a plaintext auth token exists in localStorage
  // from a previous unencrypted install, move it into the keychain and wipe it.
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && shouldIntercept(key)) {
        const existing = origGet(key);
        if (existing != null) {
          secure.set(key, existing);
          origRemove(key);
          i--; // length shrank
        }
      }
    }
  } catch (err) {
    console.warn("[secure-storage] migration failed:", err);
  }

  Object.defineProperty(window.localStorage, "getItem", {
    configurable: true,
    value: (key: string) => (shouldIntercept(key) ? secure.get(key) : origGet(key)),
  });
  Object.defineProperty(window.localStorage, "setItem", {
    configurable: true,
    value: (key: string, value: string) => {
      if (shouldIntercept(key)) {
        secure.set(key, value);
        return;
      }
      origSet(key, value);
    },
  });
  Object.defineProperty(window.localStorage, "removeItem", {
    configurable: true,
    value: (key: string) => {
      if (shouldIntercept(key)) {
        secure.remove(key);
        return;
      }
      origRemove(key);
    },
  });

  console.info(
    `[secure-storage] Supabase auth token routed through OS keychain (${window.electronRuntime.platform})`,
  );
}

install();

export {};
